import { useEffect, useState } from 'react';
import type { FC } from 'react';

type SupportedLocale = 'en' | 'zh-Hans';

type LocaleCopy = {
  skipLabel: string;
  switcher: {
    label: string;
    options: Record<SupportedLocale, string>;
  };
  title: string;
  tagline: string;
  intros: string[];
  meta: { label: string; value: string }[];
  checklistTitle: string;
  checklist: string[];
  faqTitle: string;
  faqs: { title: string; body: string }[];
  footer: string[];
};

const copy: Record<SupportedLocale, LocaleCopy> = {
  en: {
    skipLabel: 'Skip to main content',
    switcher: {
      label: 'Language',
      options: {
        en: 'English',
        'zh-Hans': '简体中文'
      }
    },
    title: 'PJConverter Support',
    tagline: 'Reliable projections, simplified.',
    intros: [
      'Need help with coordinate conversion, custom projections, or saved data? Email us anytime.',
      "Share as many details as possible and we'll respond with targeted troubleshooting steps."
    ],
    meta: [
      {
        label: 'Reply time',
        value: 'We usually respond within 2 business days'
      },
      {
        label: 'Languages',
        value: 'English · Simplified Chinese'
      }
    ],
    checklistTitle: 'Include these details in your email',
    checklist: [
      'Projection IDs or parameters you were using, plus the result you expected.',
      'Sample coordinates and any error messages or screenshots you captured.',
      'Device model, iOS version, and the PJConverter build number.'
    ],
    faqTitle: 'FAQ',
    faqs: [
      {
        title: 'Does PJConverter work offline?',
        body: 'Yes. Conversions and the bundled projection library run fully offline. Network access is only needed when downloading help docs or sharing data.'
      },
      {
        title: 'Where are custom projections stored?',
        body: 'Custom definitions stay in your Application Support/PJConverter directory on device. They remain private and can be cleared anytime from the app settings.'
      }
    ],
    footer: ['© {year} PJConverter · Built for precise coordinate workflows']
  },
  'zh-Hans': {
    skipLabel: '跳至主要内容',
    switcher: {
      label: '界面语言',
      options: {
        en: 'English',
        'zh-Hans': '简体中文'
      }
    },
    title: 'PJConverter 技术支持',
    tagline: '精准投影，简单可信。',
    intros: [
      '坐标转换、自定义投影或数据保存遇到问题？欢迎随时发邮件联系。',
      '请尽可能提供详细信息，我们会尽快给出针对性的排查建议。'
    ],
    meta: [
      {
        label: '回复节奏',
        value: '通常在 2 个工作日内答复'
      },
      {
        label: '支持语言',
        value: 'English · 简体中文'
      }
    ],
    checklistTitle: '邮件中请包含以下信息',
    checklist: [
      '使用的投影 ID 或参数，以及期望得到的结果。',
      '示例坐标、报错提示或相关截图，方便我们复现。',
      '设备型号、iOS 版本与 PJConverter 的版本号。'
    ],
    faqTitle: '常见问题',
    faqs: [
      {
        title: 'PJConverter 是否可以离线使用？',
        body: '可以。坐标转换与内置投影库全部离线运行，仅在下载帮助文档或分享数据时需要联网。'
      },
      {
        title: '自定义投影会存在哪里？',
        body: '自定义投影存放在设备的 Application Support/PJConverter 目录，可在应用设置中随时清除，数据不会上传到服务器。'
      }
    ],
    footer: ['© {year} PJConverter · 为准确坐标转换而生']
  }
};

const detectLocale = (): SupportedLocale => {
  if (typeof navigator === 'undefined') {
    return 'en';
  }

  const candidates = navigator.languages ?? [navigator.language];
  const matched = candidates.find((language) => language?.toLowerCase().startsWith('zh'));
  return matched ? 'zh-Hans' : 'en';
};

const App: FC = () => {
  const [locale, setLocale] = useState<SupportedLocale>(() => detectLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const strings = copy[locale];
  const currentYear = new Date().getFullYear();
  const localeOptions: SupportedLocale[] = ['en', 'zh-Hans'];

  const handleLocaleChange = (nextLocale: SupportedLocale) => {
    if (nextLocale !== locale) {
      setLocale(nextLocale);
    }
  };

  return (
    <div className="page" data-locale={locale}>
      <a className="skip-link" href="#main-content">
        {strings.skipLabel}
      </a>
      <main id="main-content" className="support">
        <section className="support__card" aria-labelledby="support-title">
          <header className="support__header">
            <div className="support__topbar">
              <span className="support__badge" aria-label="PJConverter">
                PJConverter
              </span>
              <div
                className="support__switcher"
                role="group"
                aria-label={strings.switcher.label}
              >
                {localeOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`support__switcher-button${
                      locale === option ? ' support__switcher-button--active' : ''
                    }`}
                    onClick={() => handleLocaleChange(option)}
                    aria-pressed={locale === option}
                  >
                    {strings.switcher.options[option]}
                  </button>
                ))}
              </div>
            </div>
            <p className="support__tagline">{strings.tagline}</p>
          </header>

          <h1 id="support-title">{strings.title}</h1>
          {strings.intros.map((paragraph) => (
            <p key={paragraph} className="support__intro">
              {paragraph}
            </p>
          ))}

          <a className="support__cta" href="mailto:pjconverter@zach.ren">
            pjconverter@zach.ren
          </a>

          <dl className="support__meta">
            {strings.meta.map(({ label, value }) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>

          <section aria-labelledby="quick-steps" className="support__section">
            <h2 id="quick-steps">{strings.checklistTitle}</h2>
            <ol className="support__list">
              {strings.checklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>

          <section aria-labelledby="faq" className="support__section support__section--secondary">
            <h2 id="faq">{strings.faqTitle}</h2>
            <ul className="support__faq">
              {strings.faqs.map(({ title, body }) => (
                <li key={title}>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </li>
              ))}
            </ul>
          </section>
        </section>

        <footer className="support__footer">
          {strings.footer.map((line) => (
            <p key={line}>{line.replace('{year}', String(currentYear))}</p>
          ))}
        </footer>
      </main>
    </div>
  );
};

export default App;
